import { KernButton } from '@kern-ux-annex/kern-react-kit'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'motion/react'
import { createContext, use, useCallback, useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'

import { getCurrentTenant, setCurrentTenant } from '../api/client'
import { useMe } from '../api/hooks'
import type { Me, TenantAccess } from '../api/types'
import { auth } from '../auth/auth'
import { leaves, PLATFORM_HOME, tenantGate } from './area'
import { hideSplash } from './splash'

const STORAGE_KEY = 'dms-tenant'

function stored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function remember(id: string | null) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* storage unavailable – the choice lives for this page only */
  }
}

/** The municipality a platform admin left last; "Zuletzt geöffnet" in the platform offers it. */
const LAST_OPENED_KEY = 'dms-plattform-zuletzt'

export function lastOpenedTenant(): string | null {
  try {
    return localStorage.getItem(LAST_OPENED_KEY)
  } catch {
    return null
  }
}

function rememberLastOpened(id: string) {
  try {
    localStorage.setItem(LAST_OPENED_KEY, id)
  } catch {
    /* storage unavailable – the platform offers no last opened municipality */
  }
}

/** Restore the last chosen municipality before the first request goes out. */
export function restoreTenant(): void {
  setCurrentTenant(stored())
}

interface TenantState {
  me: Me
  current: TenantAccess | undefined
  /** chosen explicitly (not resolved by the server): for platform admins "Kommune öffnen" */
  entered: boolean
  select: (id: string) => void
}

const TenantContext = createContext<TenantState | null>(null)

export function useTenantState(): TenantState {
  const state = use(TenantContext)
  if (!state) throw new Error('TenantProvider missing')
  return state
}

/** Optional variant for components that may render outside the provider. */
export function useCurrentTenant(): TenantAccess | undefined {
  return use(TenantContext)?.current
}

export function useSelectTenant(): (id: string) => void {
  const client = useQueryClient()
  return useCallback(
    (id: string) => {
      if (id === getCurrentTenant()) return
      remember(id)
      setCurrentTenant(id)
      // all cached data belongs to the previous municipality
      void client.resetQueries()
    },
    [client],
  )
}

/**
 * Leaves the entered municipality (map „Plattform-Bereich“, decision 2026-11-03): no municipality
 * is chosen any more, and the platform remembers it as the last opened one.
 */
export function useLeaveTenant(): () => void {
  const client = useQueryClient()
  return useCallback(() => {
    const left = getCurrentTenant()
    if (!left) return
    rememberLastOpened(left)
    remember(null)
    setCurrentTenant(null)
    // the data of the municipality goes; the platform's own queries stay on screen
    client.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'admin' })
    void client.invalidateQueries({ queryKey: ['me'] })
  }, [client])
}

/**
 * Resolves the municipality of the session. Renders children only once it is known – except on
 * the pages of the platform, which work without one (see ``tenantGate``).
 */
export function TenantProvider({ children, loading }: { children: ReactNode; loading: ReactNode }) {
  const { pathname } = useLocation()
  const me = useMe()
  const select = useSelectTenant()
  const leave = useLeaveTenant()

  // the server resolved a municipality (e.g. the only one): members adopt it for further
  // requests; platform admins enter a municipality only explicitly ("Kommune öffnen")
  const resolved = me.data?.currentTenantId ?? null
  const platformAdmin = me.data?.platformAdmin ?? false
  useEffect(() => {
    if (resolved && !platformAdmin && resolved !== getCurrentTenant()) {
      remember(resolved)
      setCurrentTenant(resolved)
    }
    if (!resolved && me.data && getCurrentTenant()) {
      // the remembered municipality is not accessible any more
      remember(null)
      setCurrentTenant(null)
    }
  }, [resolved, platformAdmin, me.data])
  // entering the platform leaves the entered municipality, however one gets there
  useEffect(() => {
    if (me.data && leaves({ pathname, platformAdmin, entered: Boolean(getCurrentTenant()) })) leave()
  }, [pathname, platformAdmin, me.data, leave])
  useEffect(() => {
    if (!me.isPending) hideSplash()
  }, [me.isPending])

  if (me.isPending) return <>{loading}</>
  if (me.isError) throw me.error
  const data = me.data
  const current = data.tenants.find((t) => t.id === data.currentTenantId)
  const entered = Boolean(current) && getCurrentTenant() === current?.id
  const state = { me: data, current, entered, select }

  const gate = tenantGate({
    pathname,
    platformAdmin: data.platformAdmin,
    hasTenant: Boolean(current),
    tenantCount: data.tenants.length,
  })
  if (gate === 'to-platform') return <Navigate to={PLATFORM_HOME} replace />
  if (gate !== 'page') {
    return (
      <TenantContext value={state}>
        <main id="main" className="mx-auto w-full max-w-[1600px] px-24 py-24">
          {gate === 'chooser' ? <TenantChooser tenants={data.tenants} onSelect={select} /> : <NoAccess me={data} />}
        </main>
      </TenantContext>
    )
  }
  return <TenantContext value={state}>{children}</TenantContext>
}

function TenantChooser({ tenants, onSelect }: { tenants: TenantAccess[]; onSelect: (id: string) => void }) {
  return (
    <section className="flex max-w-[48rem] flex-col gap-16 py-24">
      <h1 className="kern-heading kern-heading-large">Kommune auswählen</h1>
      <p className="kern-body dms-muted">Ihr Konto hat Zugriff auf mehrere Kommunen. Mit welcher möchten Sie arbeiten?</p>
      <ul className="grid grid-cols-1 gap-12 sm:grid-cols-2">
        {tenants.map((t, i) => (
          <motion.li key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <button
              type="button"
              onClick={() => onSelect(t.id)}
              className="dms-card dms-focus flex w-full flex-col items-start gap-4 p-16 text-left hover:border-[var(--dms-action)]"
            >
              <span className="text-kern-20 font-semibold">{t.name}</span>
              <span className="dms-muted text-kern-16">
                {t.active ? '' : 'deaktiviert · '}
                {t.slug}
              </span>
            </button>
          </motion.li>
        ))}
      </ul>
    </section>
  )
}

function NoAccess({ me }: { me: Me }) {
  return (
    <section className="flex max-w-[48rem] flex-col gap-16 py-24">
      <h1 className="kern-heading kern-heading-large">Noch kein Zugang</h1>
      <p className="kern-body">
        Sie sind als <strong>{me.email ?? me.name}</strong> angemeldet. Ihrem Konto ist noch keine Kommune
        zugeordnet. Bitte wenden Sie sich an die Administration von Drittmittelscan.
      </p>
      {me.platformAdmin && (
        <p className="kern-body">
          Als Plattform-Administration können Sie in der <a className="kern-link kern-link--no-visited-state" href={PLATFORM_HOME}>Plattform</a> die
          erste Kommune anlegen.
        </p>
      )}
      {auth().enabled && (
        <div>
          <KernButton variant="secondary" icon="logout" label="Abmelden" onClick={auth().logout} />
        </div>
      )}
    </section>
  )
}
